import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { trackEvent } from "../lib/api";
import {
  getIntakeOptions,
  resumeSession,
  submitAudit,
} from "../lib/api/index";
import { logger } from "../lib/logger";

// Phase 5a — post-audit "business discovery" intake.
//
// Renamed from AuditEmail.jsx. Captures 4 required contact fields plus
// 14 optional business-profile / growth-intent fields. The audit's q1
// industry was captured during the diagnostic flow itself (with the
// optional `industry_other` follow-up); this page does NOT re-ask for it.
//
// All option lists for dropdowns come from GET /api/audit/intake-options
// — a single backend source of truth defined in audit/intake_options.py.

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_CHARS_RE = /^[\d+\-() ]+$/;

const EMPTY_FORM = {
  owner_name: "",
  email: "",
  business_name: "",
  phone: "",
  website: "",
  city: "",
  state: "",
  // business_profile
  years_in_business: "",
  employee_count: "",
  annual_revenue_range: "",
  monthly_revenue_range: "",
  primary_service: "",
  primary_lead_source: "",
  // growth_intent
  biggest_challenge: "",
  biggest_opportunity: "",
  growth_goal_12mo: "",
};

const LABEL_STYLE = {
  display: "block", fontSize: 12, color: "var(--sf-muted)",
  letterSpacing: "0.12em", textTransform: "uppercase",
  fontFamily: "var(--sf-mono, monospace)", marginBottom: 6,
};
const FIELD_GRID = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 };
const SECTION_HEADER = { fontSize: 18, marginBottom: 4 };
const SECTION_HINT = { fontSize: 13, color: "var(--sf-muted)", marginBottom: 16 };

function Field({ name, label, required, error, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label htmlFor={`intake-${name}`} style={LABEL_STYLE}>
        {label}{required ? " *" : ""}
      </label>
      {children}
      {error && (
        <div
          className="sf-mono"
          style={{ color: "#e2867e", fontSize: 12, marginTop: 4 }}
          data-testid={`intake-error-${name}`}
        >
          {error}
        </div>
      )}
    </div>
  );
}

export default function AuditIntake() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialSid = location.state?.sessionId || null;

  const [sessionId, setSessionId] = useState(initialSid);
  const [resolving, setResolving] = useState(!initialSid);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [globalError, setGlobalError] = useState(null);
  const [options, setOptions] = useState(null);

  // Mount event — server-rendered analytics counter for the new intake page.
  useEffect(() => {
    trackEvent("intake_form_view", "/audit/intake", { platform: "web" });
  }, []);

  // Pull the canonical option lists. If this fails, dropdowns degrade to
  // empty selects — the user can still submit with the 4 required fields,
  // which keeps the intake unblocked in a network-flake scenario.
  useEffect(() => {
    let cancelled = false;
    getIntakeOptions()
      .then((data) => { if (!cancelled) setOptions(data?.options || {}); })
      .catch((err) => {
        if (cancelled) return;
        logger.error("intake_options_load_failed", { err: err?.message });
        setOptions({});
      });
    return () => { cancelled = true; };
  }, []);

  // If we did not arrive with a sessionId in router state (e.g., page refresh),
  // recover it from the signed cookie. Same pattern as the previous Email page.
  useEffect(() => {
    if (sessionId) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await resumeSession();
        if (cancelled) return;
        if (data?.session_id) {
          setSessionId(data.session_id);
        } else {
          navigate("/audit");
        }
      } catch (err) {
        logger.error("intake_resume_failed", { status: err?.response?.status });
        navigate("/audit");
      } finally {
        if (!cancelled) setResolving(false);
      }
    })();
    return () => { cancelled = true; };
  }, [sessionId, navigate]);

  const setField = useCallback((name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  // Client-side gate on required fields ONLY — keeps the gate friendly. Other
  // validation (lengths, formats) is enforced server-side; we render whatever
  // field-level errors the server returns.
  const requiredFilled = Boolean(
    form.owner_name.trim() && form.business_name.trim()
    && EMAIL_RE.test(form.email.trim())
    && form.phone.trim().length >= 7
    && PHONE_CHARS_RE.test(form.phone.trim()),
  );

  const onSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!sessionId) {
      setGlobalError("Session expired. Please restart the audit.");
      return;
    }
    setErrors({});
    setGlobalError(null);
    setSubmitting(true);

    // Compose payload — empty strings become null so the backend stores nulls.
    const trimOrNull = (s) => {
      const t = (s || "").trim();
      return t ? t : null;
    };
    const profile = {
      years_in_business: trimOrNull(form.years_in_business),
      employee_count: trimOrNull(form.employee_count),
      annual_revenue_range: trimOrNull(form.annual_revenue_range),
      monthly_revenue_range: trimOrNull(form.monthly_revenue_range),
      primary_service: trimOrNull(form.primary_service),
      primary_lead_source: trimOrNull(form.primary_lead_source),
    };
    const intent = {
      biggest_challenge: trimOrNull(form.biggest_challenge),
      biggest_opportunity: trimOrNull(form.biggest_opportunity),
      growth_goal_12mo: trimOrNull(form.growth_goal_12mo),
    };
    const anyProfile = Object.values(profile).some((v) => v !== null);
    const anyIntent = Object.values(intent).some((v) => v !== null);

    const payload = {
      session_id: sessionId,
      owner_name: form.owner_name.trim(),
      email: form.email.trim(),
      business_name: form.business_name.trim(),
      phone: form.phone.trim(),
      website: trimOrNull(form.website),
      city: trimOrNull(form.city),
      state: trimOrNull(form.state),
      business_profile: anyProfile ? profile : null,
      growth_intent: anyIntent ? intent : null,
    };

    try {
      const data = await submitAudit(payload);
      trackEvent(
        "intake_form_submit_success",
        "/audit/intake",
        {
          platform: "web",
          has_profile: anyProfile,
          has_intent: anyIntent,
          fields_filled: Object.values(payload).filter(
            (v) => v !== null && v !== "" && v !== sessionId,
          ).length,
        },
        sessionId,
      );
      navigate(`/audit/preview/${data.session_id}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 422 && detail && typeof detail === "object" && detail.errors) {
        setErrors(detail.errors);
        setGlobalError("Please correct the highlighted fields and try again.");
      } else if (detail?.error === "incomplete_session") {
        setGlobalError("Some audit answers were missing. Please re-take the diagnostic.");
      } else {
        logger.error("intake_submit_failed", { err: err?.response?.data || err?.message });
        setGlobalError("Could not submit your audit. Try again in a moment.");
      }
      setSubmitting(false);
    }
  }, [submitting, sessionId, form, navigate]);

  if (resolving) {
    return (
      <div className="sf-container" style={{ padding: "80px 24px" }} data-testid="audit-intake-resolving">
        <div className="sf-muted-text">Locating your session…</div>
      </div>
    );
  }

  const opt = (name) => (options?.[name] || []);

  return (
    <div className="sf-container" style={{ padding: "48px 24px", maxWidth: 760 }} data-testid="audit-intake-page">
      <div className="sf-eyebrow" style={{ marginBottom: 10 }}>Step 13 of 13</div>
      <h1 className="sf-display" style={{ fontSize: 34, marginBottom: 10, lineHeight: 1.15 }}>
        You&rsquo;ve completed the diagnostic.
      </h1>
      <p className="sf-muted-text" style={{ marginBottom: 28 }}>
        Tell us a little more so we can prepare your audit findings.
      </p>

      <form className="sf-card" onSubmit={onSubmit} data-testid="audit-intake-form" noValidate>
        {/* --- Contact Information (required) ----------------------------- */}
        <div style={{ marginBottom: 6 }}>
          <h2 className="sf-display" style={SECTION_HEADER}>Contact information</h2>
          <p style={SECTION_HINT}>Required &mdash; we use this to send your audit.</p>
        </div>
        <div style={FIELD_GRID}>
          <Field name="owner_name" label="Full name" required error={errors.owner_name}>
            <input
              id="intake-owner_name" type="text" className="sf-input"
              value={form.owner_name}
              onChange={(e) => setField("owner_name", e.target.value)}
              placeholder="Jane Smith" autoComplete="name"
              data-testid="intake-owner-name-input"
            />
          </Field>
          <Field name="business_name" label="Business name" required error={errors.business_name}>
            <input
              id="intake-business_name" type="text" className="sf-input"
              value={form.business_name}
              onChange={(e) => setField("business_name", e.target.value)}
              placeholder="Northwind Consulting" autoComplete="organization"
              data-testid="intake-business-name-input"
            />
          </Field>
          <Field name="email" label="Email" required error={errors.email}>
            <input
              id="intake-email" type="email" className="sf-input"
              value={form.email}
              onChange={(e) => setField("email", e.target.value)}
              placeholder="founder@yourcompany.com" autoComplete="email"
              data-testid="intake-email-input"
            />
          </Field>
          <Field name="phone" label="Phone" required error={errors.phone}>
            <input
              id="intake-phone" type="tel" className="sf-input"
              value={form.phone}
              onChange={(e) => setField("phone", e.target.value)}
              placeholder="+1 (415) 555-0123" autoComplete="tel"
              data-testid="intake-phone-input"
            />
          </Field>
        </div>

        {/* --- Business Details (optional) -------------------------------- */}
        <div style={{ marginTop: 24, marginBottom: 6 }}>
          <h2 className="sf-display" style={SECTION_HEADER}>Business details</h2>
          <p style={SECTION_HINT}>Optional, but helps us prepare a sharper audit.</p>
        </div>
        <div style={FIELD_GRID}>
          <Field name="website" label="Website" error={errors.website}>
            <input
              id="intake-website" type="text" className="sf-input"
              value={form.website}
              onChange={(e) => setField("website", e.target.value)}
              placeholder="yourcompany.com"
              data-testid="intake-website-input"
            />
          </Field>
          <div /> {/* spacer for alignment */}
          <Field name="city" label="City" error={errors.city}>
            <input
              id="intake-city" type="text" className="sf-input"
              value={form.city}
              onChange={(e) => setField("city", e.target.value)}
              placeholder="San Francisco" autoComplete="address-level2"
              data-testid="intake-city-input"
            />
          </Field>
          <Field name="state" label="State / region" error={errors.state}>
            <input
              id="intake-state" type="text" className="sf-input"
              value={form.state}
              onChange={(e) => setField("state", e.target.value)}
              placeholder="CA" autoComplete="address-level1"
              data-testid="intake-state-input"
            />
          </Field>
        </div>

        {/* --- Business Profile (optional) -------------------------------- */}
        <div style={{ marginTop: 24, marginBottom: 6 }}>
          <h2 className="sf-display" style={SECTION_HEADER}>Business profile</h2>
          <p style={SECTION_HINT}>Optional &mdash; powers benchmark and recommendation context.</p>
        </div>
        <div style={FIELD_GRID}>
          <Field name="years_in_business" label="Years in business">
            <select
              id="intake-years_in_business" className="sf-input"
              value={form.years_in_business}
              onChange={(e) => setField("years_in_business", e.target.value)}
              data-testid="intake-years-select"
            >
              <option value="">Select…</option>
              {opt("years_in_business").map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field name="employee_count" label="Employees">
            <select
              id="intake-employee_count" className="sf-input"
              value={form.employee_count}
              onChange={(e) => setField("employee_count", e.target.value)}
              data-testid="intake-employees-select"
            >
              <option value="">Select…</option>
              {opt("employee_count").map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field name="annual_revenue_range" label="Annual revenue">
            <select
              id="intake-annual_revenue_range" className="sf-input"
              value={form.annual_revenue_range}
              onChange={(e) => setField("annual_revenue_range", e.target.value)}
              data-testid="intake-annual-revenue-select"
            >
              <option value="">Select…</option>
              {opt("annual_revenue_range").map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <Field name="monthly_revenue_range" label="Monthly revenue">
            <select
              id="intake-monthly_revenue_range" className="sf-input"
              value={form.monthly_revenue_range}
              onChange={(e) => setField("monthly_revenue_range", e.target.value)}
              data-testid="intake-monthly-revenue-select"
            >
              <option value="">Select…</option>
              {opt("monthly_revenue_range").map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
        </div>
        <Field name="primary_service" label="Primary service or product" error={errors.primary_service}>
          <input
            id="intake-primary_service" type="text" className="sf-input"
            value={form.primary_service}
            onChange={(e) => setField("primary_service", e.target.value)}
            placeholder="e.g. Done-for-you Facebook ads for dentists"
            maxLength={280}
            data-testid="intake-primary-service-input"
          />
        </Field>
        <Field name="primary_lead_source" label="Primary lead source">
          <select
            id="intake-primary_lead_source" className="sf-input"
            value={form.primary_lead_source}
            onChange={(e) => setField("primary_lead_source", e.target.value)}
            data-testid="intake-lead-source-select"
          >
            <option value="">Select…</option>
            {opt("primary_lead_source").map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>

        {/* --- Growth Intent (optional) ----------------------------------- */}
        <div style={{ marginTop: 24, marginBottom: 6 }}>
          <h2 className="sf-display" style={SECTION_HEADER}>Growth intent</h2>
          <p style={SECTION_HINT}>Optional &mdash; helps us calibrate the right level of recommendation.</p>
        </div>
        <Field name="biggest_challenge" label="Biggest challenge right now" error={errors.biggest_challenge}>
          <textarea
            id="intake-biggest_challenge" className="sf-textarea"
            rows={2}
            value={form.biggest_challenge}
            onChange={(e) => setField("biggest_challenge", e.target.value)}
            placeholder="What&rsquo;s the single thing slowing you down most?"
            maxLength={500}
            data-testid="intake-challenge-input"
          />
        </Field>
        <Field name="biggest_opportunity" label="Biggest opportunity right now" error={errors.biggest_opportunity}>
          <textarea
            id="intake-biggest_opportunity" className="sf-textarea"
            rows={2}
            value={form.biggest_opportunity}
            onChange={(e) => setField("biggest_opportunity", e.target.value)}
            placeholder="Where&rsquo;s the next big lever you haven&rsquo;t pulled?"
            maxLength={500}
            data-testid="intake-opportunity-input"
          />
        </Field>
        <Field name="growth_goal_12mo" label="12-month growth goal">
          <select
            id="intake-growth_goal_12mo" className="sf-input"
            value={form.growth_goal_12mo}
            onChange={(e) => setField("growth_goal_12mo", e.target.value)}
            data-testid="intake-growth-goal-select"
          >
            <option value="">Select…</option>
            {opt("growth_goal_12mo").map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </Field>

        {globalError && (
          <div
            className="sf-mono"
            style={{ color: "#e2867e", fontSize: 13, marginTop: 18 }}
            data-testid="intake-global-error"
          >
            {globalError}
          </div>
        )}

        <div style={{ display: "flex", gap: 12, marginTop: 26 }}>
          <button
            type="submit"
            disabled={submitting || !requiredFilled}
            className="sf-btn"
            data-testid="intake-submit-btn"
          >
            {submitting ? "Generating audit…" : "Generate my audit"}
          </button>
        </div>
      </form>
    </div>
  );
}
